import { Button, Popover as SPopover } from '@atgfr8/atg-ui-tailwind';
import { type JSX, createEffect, createSignal } from 'solid-js';

export interface PopoverAPI {
  close: () => void;
}

export type PopoverProps = {
  eleName: string | JSX.Element;
  children: JSX.Element;
  classes?: string;
  parenDivStyle?: string;
  sxProps?: JSX.CSSProperties;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  class?: string;
  onClick?: () => void;
  preventFormSubmit?: boolean;
  forceClosePopover?: boolean;
  triggerClose?: boolean;
  ref?: (api: PopoverAPI) => void;
  /** Called when the popover is dismissed by clicking outside or pressing Escape. */
  onOutsideClose?: () => void;
};

const Popover = (props: PopoverProps) => {
  const [anchorEl, setAnchorEl] = createSignal<HTMLButtonElement | null>(null);

  const handleClick = (
    event: MouseEvent & { currentTarget: HTMLButtonElement },
  ) => {
    props.onClick && props.onClick();

    if (Boolean(props.forceClosePopover)) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOutsideClose = () => {
    handleClose();
    props.onOutsideClose?.();
  };

  // Expose close API via ref callback
  props.ref?.({ close: handleClose });

  const open = () => {
    return Boolean(anchorEl());
  };

  const id = () => (open() ? 'simple-popover' : undefined);

  createEffect(() => {
    if (Boolean(props.triggerClose)) {
      handleClose();
    }
  });

  return (
    <div class={props.parenDivStyle}>
      {Boolean(props.eleName) && (
        <Button
          aria-describedby={id()}
          type={props.preventFormSubmit ? 'button' : undefined}
          onMouseDown={handleClick}
          class={props.classes}
          sx={props.sxProps}
          variant="text"
          dataTestId={`popover-trigger-${typeof props.eleName === 'string' ? props.eleName : 'content'}`}
        >
          {props.eleName}
        </Button>
      )}
      <SPopover
        id={id()}
        open={open()}
        anchorEl={anchorEl()}
        onClose={handleOutsideClose}
        class={props.class}
        dataTestId={`popover-content-${id() ?? 'closed'}`}
        anchorOrigin={
          props.anchorOrigin
            ? props.anchorOrigin
            : {
                vertical: 'bottom',
                horizontal: 'left',
              }
        }
        transformOrigin={
          props.transformOrigin
            ? props.transformOrigin
            : {
                vertical: 'top',
                horizontal: 'left',
              }
        }
      >
        {props.children}
      </SPopover>
    </div>
  );
};

export default Popover;
